import classNames from 'classnames';
import {
    ChangeEvent,
    FocusEvent,
    ForwardedRef,
    InputEvent,
    InputHTMLAttributes,
    MouseEvent,
    PointerEvent,
    useEffect,
    useRef,
} from 'react';

import { mergeRefs } from '../../lib/utils';

import styles from '../../styles/fields/text-field.module.css';

export interface TextFieldProps extends Omit<
    InputHTMLAttributes<HTMLInputElement>,
    'onChange' | 'onInput' | 'onFocus' | 'onBlur' | 'onPointerUp' | 'onClick'
> {
    type?: 'text' | 'email' | 'tel';
    name?: string | null;
    id?: string | null;
    value?: string | null;
    errors?: string[] | null;
    placeholder?: string | null;
    autoComplete?: string | null;
    withoutAutocomplete?: boolean;
    withBorder?: boolean;
    required?: boolean;
    disabled?: boolean;
    selectOnFocus?: boolean;
    readOnly?: boolean;
    className?: string | null;
    onChange?: (value: string | null, e: ChangeEvent<HTMLInputElement>) => void;
    onInput?: (e: InputEvent<HTMLInputElement>) => void;
    onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
    onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
    onPointerUp?: (e: PointerEvent<HTMLInputElement>) => void;
    onClick?: (e: MouseEvent<HTMLInputElement>) => void;
}

function TextField({
    type = 'text',
    name = null,
    id = null,
    value = null,
    errors = null,
    placeholder = null,
    autoComplete = null,
    withoutAutocomplete = false,
    withBorder = false,
    required = false,
    disabled = false,
    selectOnFocus = false,
    readOnly = false,
    className = null,
    onChange = null,
    onInput: onCustomInput = null,
    onFocus = null,
    onBlur = null,
    onPointerUp = null,
    onClick = null,
    ref: inputRef = null,
}: TextFieldProps & { ref?: ForwardedRef<HTMLInputElement> | null }) {
    const internalRef = useRef<HTMLInputElement>(null);

    const onInputFocus = (e: FocusEvent<HTMLInputElement>) => {
        if (selectOnFocus) {
            e.currentTarget.select();
        }
        if (onFocus !== null) {
            onFocus(e);
        }
    };

    const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (errors !== null && internalRef.current) {
            internalRef.current.setCustomValidity('');
            // internalRef.current.reportValidity();
        }
        const newValue = e.currentTarget.value !== '' ? e.currentTarget.value : null;
        if (onChange !== null) {
            onChange(newValue, e);
        }
    };

    const onInput = (e: InputEvent<HTMLInputElement>) => {
        if (errors !== null && internalRef.current) {
            internalRef.current.setCustomValidity('');
        }
        if (onCustomInput !== null) {
            onCustomInput(e);
        }
    };

    useEffect(() => {
        if (internalRef.current) {
            internalRef.current.setCustomValidity(
                errors !== null && errors.length > 0 ? errors[0] : '',
            );
            if (errors !== null && errors.length > 0) {
                internalRef.current.reportValidity();
            }
        }
    }, [errors]);

    return (
        <input
            ref={mergeRefs(internalRef, inputRef)}
            type={type}
            name={name ?? undefined}
            id={id ?? undefined}
            value={value ?? ''}
            placeholder={placeholder ?? undefined}
            required={required}
            disabled={disabled}
            readOnly={readOnly}
            className={classNames([
                styles.container,
                {
                    [styles.withBorder]: withBorder,
                },
                className,
            ])}
            onChange={onInputChange}
            onInput={onInput}
            onFocus={onInputFocus}
            onBlur={onBlur}
            onPointerUp={onPointerUp}
            onClick={onClick}
            autoComplete={autoComplete ?? (withoutAutocomplete ? 'off' : undefined)}
        />
    );
}

export default TextField;
